import React from 'react';
import { LeaderboardEntry, PersonalizedLeaderboardEntry, NO_CURSOR_DATA } from '../types';
import { formatTimeMinutes } from '../services/personalizedDataService';
import ExportButtons from './ExportButtons';
import { exportLeaderboardToCSV, exportLeaderboardToPDF, LeaderboardExportOptions } from '../services/leaderboardExportService';
import type { CycleDateRange } from '../services/cursorSpendService';
import { formatSpendDisplay } from '../services/cursorSpendService';

interface LeaderboardTableProps {
  users: LeaderboardEntry[] | PersonalizedLeaderboardEntry[];
  sortBy: string;
  onSortChange: (metric: string) => void;
  isPersonalized?: boolean;
  exportOptions?: LeaderboardExportOptions;
  /** Billing cycle date range for spend columns (from Cursor API). */
  cycleDateRange?: CycleDateRange | null;
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({ 
  users, 
  sortBy, 
  onSortChange, 
  isPersonalized = false,
  exportOptions,
  cycleDateRange
}) => {
  // Define the base metrics that will be displayed in the table with new fields
  const baseMetrics: Array<{
    key: keyof Omit<LeaderboardEntry, 'userId' | 'email'>;
    label: string;
    /** Optional column tooltip (overrides sort hint when set). */
    headerTitle?: string;
  }> = [
    {
      key: 'spendPerUser',
      label: 'Spend (Cycle to Date)',
      headerTitle:
        'Total cycle spend and (on-demand) overage on top of included allowance. Click to sort.',
    },
    { key: 'chatTotalApplies', label: 'Chat Total Applies' },
    { key: 'chatTotalAccepts', label: 'Chat Total Accepts' },
    { key: 'chatAcceptedLinesAdded', label: 'Chat Accepted Lines Added' },
    { key: 'totalLinesAdded', label: 'Total Lines Added' },
    { key: 'chatTabsShown', label: 'Chat Tabs Shown' },
    { key: 'tabsAccepted', label: 'Tabs Accepted' },
    { key: 'editRequests', label: 'Edit Requests' },
    { key: 'askRequests', label: 'Ask Requests' },
    { key: 'agentRequests', label: 'Agent Requests' },
    { key: 'cmdkRequests', label: 'Cmd+K Requests' },
  ];

  // Additional metrics for personalized mode (efficiency first, then cursor time)
  const personalizedMetrics: Array<{
    key: keyof PersonalizedLeaderboardEntry;
    label: string;
  }> = [
    { key: 'efficiencyScore', label: 'Efficiency Score (Lines/Hour)' },
    { key: 'cursorTimeMinutes', label: 'Cursor Time' },
  ];

  type DisplayMetric = {
    key: string;
    label: string;
    sortable: boolean;
    /** Column tooltip when set */
    headerTitle?: string;
  };

  // Determine which metrics to display
  const staticMetrics = isPersonalized ? [...personalizedMetrics, ...baseMetrics] : baseMetrics;
  const displayMetrics: DisplayMetric[] = staticMetrics.map((metric) => ({
    key: metric.key as string,
    label: metric.label,
    sortable: true,
    headerTitle: 'headerTitle' in metric && typeof metric.headerTitle === 'string' ? metric.headerTitle : undefined,
  }));

  /**
   * Check if a user is a PersonalizedLeaderboardEntry
   */
  const isPersonalizedUser = (user: LeaderboardEntry | PersonalizedLeaderboardEntry): user is PersonalizedLeaderboardEntry => {
    return isPersonalized && 'teamMemberName' in user;
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for CSV export');
      return;
    }
    exportLeaderboardToCSV(users, exportOptions);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for PDF export');
      return;
    }
    exportLeaderboardToPDF(users, exportOptions);
  };



  /**
   * Format cell value based on metric type and field mapping
   */
  const formatCellValue = (
    user: LeaderboardEntry | PersonalizedLeaderboardEntry, 
    metricKey: string
  ): string | number => {
    const value = (user as any)[metricKey];
    
    // Handle missing data (represented as '-', null/undefined, or NO_CURSOR_DATA)
    if (value === '-' || value === null || value === undefined || value === NO_CURSOR_DATA) {
      return '-';
    }
    
    // Static formatting - no field mapping needed
    
    // Special handling for personalized metrics (backward compatibility)
    if (metricKey === 'cursorTimeMinutes' && typeof value === 'number') {
      return formatTimeMinutes(value);
    }
    
    if (metricKey === 'efficiencyScore' && typeof value === 'number') {
      return value.toFixed(2);
    }
    
    // Spend: total + (on-demand); merge sets spendOnDemandPerUser when spend data exists
    if (metricKey === 'spendPerUser') {
      if (typeof value === 'number') {
        const onDemand = (user as LeaderboardEntry).spendOnDemandPerUser;
        return formatSpendDisplay(value, onDemand);
      }
      return '-';
    }
    
    // Static field formatting
    
    // Return numeric values as-is, convert others to string
    return typeof value === 'number' ? value : String(value);
  };

  return (
    <div className="leaderboard-table">
      <h2>
        {isPersonalized ? 'Enhanced AI Leaderboard' : 'AI Users Leaderboard'}
        {isPersonalized && (
          <span className="table-subtitle">
            (AI Tools + Team Data Combined)
          </span>
        )}
      </h2>
      
      <div className="table-stats">
        <div>
          <span>Total {isPersonalized ? 'Team Members' : 'Users'}: {users.length}</span>
          {cycleDateRange && (
            <span className="cycle-date-range" title="Billing cycle from Cursor">
              {' • '}Cycle: {cycleDateRange.label}
            </span>
          )}
          {isPersonalized && users.length > 0 && (
            <span>
              • Avg Efficiency: {
                (users as PersonalizedLeaderboardEntry[])
                  .reduce((sum, user) => sum + user.efficiencyScore, 0) / users.length
              } lines/hour
            </span>
          )}
        </div>
        
        {exportOptions && (
          <ExportButtons
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            disabled={users.length === 0}
            className="cursor-export-container"
          />
        )}
      </div>
      
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              {isPersonalized && <th>Team Member</th>}
              {!isPersonalized && <th>Email</th>}
              {displayMetrics.map((metric) => (
                <th
                  key={metric.key}
                  className={`${sortBy === metric.key ? 'active-sort' : ''} ${metric.sortable ? 'sortable' : 'non-sortable'}`}
                  onClick={() => metric.sortable && onSortChange(metric.key)}
                  title={
                    metric.sortable
                      ? metric.headerTitle ?? `Sort by ${metric.label}`
                      : `${metric.label} (not sortable)`
                  }
                  style={{ cursor: metric.sortable ? 'pointer' : 'default' }}
                >
                  {metric.label} {sortBy === metric.key ? '▼' : ''}
                  {!metric.sortable && (
                    <span className="non-sortable-indicator" title="This field cannot be used for sorting"> 📝</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.userId} className={index < 3 ? `rank-${index + 1}` : ''}>
                <td className="rank-cell">
                  <span className="rank-number">{index + 1}</span>
                  {index === 0 && <span className="rank-badge gold">🥇</span>}
                  {index === 1 && <span className="rank-badge silver">🥈</span>}
                  {index === 2 && <span className="rank-badge bronze">🥉</span>}
                </td>
                                 {isPersonalized && isPersonalizedUser(user) && (
                   <td className="team-member-cell">
                     <strong>{user.teamMemberName}</strong>
                   </td>
                 )}
                 {!isPersonalized && <td className="email-cell">{user.email}</td>}
                {displayMetrics.map((metric) => (
                  <td 
                    key={`${user.userId}-${metric.key}`}
                    className={`
                      ${metric.key === 'efficiencyScore' ? 'efficiency-cell' : ''}
                      ${!metric.sortable ? 'non-sortable-cell' : ''}
                    `.trim()}
                  >
                    {formatCellValue(user, metric.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {users.length === 0 && (
        <div className="no-data-message">
          No {isPersonalized ? 'team members' : 'users'} to display
        </div>
      )}
    </div>
  );
};

export default LeaderboardTable; 